import { Component, inject, signal } from '@angular/core';
import { RouterOutlet, RouterLinkWithHref } from '@angular/router';
import { LoggingService, TdoeLayoutComponent, NavItem } from '@tdoe/design-system';
import { DrupalService } from './services/drupal-service/drupal-service';
import { combineLatest, map, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { PillarNavItem } from './models/pillar-nav-item';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MatIconModule } from '@angular/material/icon';
import packageInfo from '../../package.json';

type FrtnNavItem = Omit<NavItem, 'children'> & {
  target?: string;
  children?: FrtnNavItem[];
};

@Component({
  selector: 'app-root',  standalone: true,  imports: [
    RouterOutlet,
    TdoeLayoutComponent,
    CommonModule,
    RouterLinkWithHref,
    MatIconModule,
    AngularSvgIconModule
],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  protected readonly title = signal('tdoe-frtn');

  public menuOpen = false;

  private readonly drupalService = inject(DrupalService);
  private readonly logger = inject(LoggingService);

  public hoveredLevel1: FrtnNavItem | null = null;
  public hoveredLevel2: FrtnNavItem | null = null;
  public hoveredLevel3: FrtnNavItem | null = null;
  public mobileMenuOpen = false;
  public expandedMobileItems = new Set<string>();

  public readonly viewModel$ = combineLatest([
    this.drupalService.getNavItems(),
    this.drupalService.getFooter()
  ]).pipe(
    map(([navItems, footer]) => ({navItems, footer})),
    map(data => ({
      navItems: this.convertNavItems(data.navItems),
      footerContent: data.footer.at(0),
      clientVersion: packageInfo.version
    })),
    tap(_ => {
      this.logger.debug('App -> viewModel$', { data: { _ }});
    })
  );

  private convertNavItems(pillarNavItems?: PillarNavItem[]): FrtnNavItem[] {
    const result = pillarNavItems?.map(item => ({
      text: item.title,
      url: item.relative,
      target: item.options?.attributes?.target,
      children: this.convertNavItems(item.below)
    } as FrtnNavItem));

    return result ?? [];
}

  public setHoveredItem(item: FrtnNavItem, level: number): void {
    if (level === 1) {
      this.hoveredLevel1 = item;
      this.hoveredLevel2 = null;
      this.hoveredLevel3 = null;
    } else if (level === 2) {
      this.hoveredLevel2 = item;
      this.hoveredLevel3 = null;
    } else if (level === 3) {
      this.hoveredLevel3 = item;
    }
  }

  public clearHoveredItem(level: number): void {
    if (level === 1) {
      this.hoveredLevel1 = null;
      this.hoveredLevel2 = null;
      this.hoveredLevel3 = null;
    } else if (level === 2) {
      this.hoveredLevel2 = null;
      this.hoveredLevel3 = null;
    } else if (level === 3) {
      this.hoveredLevel3 = null;
    }
  }

  public toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    if (!this.mobileMenuOpen) {
      this.expandedMobileItems.clear();
    }
  }

  public toggleMobileItem(itemText: string): void {
    if (this.expandedMobileItems.has(itemText)) {
      this.expandedMobileItems.delete(itemText);
    } else {
      this.expandedMobileItems.add(itemText);
    }
  }

  public isMobileItemExpanded(itemText: string): boolean {
    return this.expandedMobileItems.has(itemText);
  }

}
